import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { TrendingUp, TrendingDown, Landmark } from "lucide-react";
import api from "../lib/api";
import { logError } from "../lib/logger";

const fmtUsd = (v) => `${v >= 0 ? "+" : "-"}${Math.abs(v).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getFullYear()).slice(2)}`;
  } catch { return "—"; }
};

function Summ({ label, value, color }) {
  return (
    <div className="bg-black/30 border border-white/10 rounded-md p-3">
      <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{label}</div>
      <div className={`font-mono text-lg font-semibold mt-0.5 ${color || "text-white"}`}>{value}</div>
    </div>
  );
}

export function MtClosedTradesModal({ open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/mt/closed-trades")
      .then((r) => setData(r.data))
      .catch((e) => logError("Errore trade chiusi MT5:", e))
      .finally(() => setLoading(false));
  }, [open]);

  const trades = data?.trades || [];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="mt-closed-trades-modal" className="max-w-2xl bg-[#0d0d0d] border-blue-500/20 text-white max-h-[85vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-white/10">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Landmark size={18} className="text-blue-400" /> Trade chiusi · Conto reale MT5
          </DialogTitle>
          <p className="text-xs text-zinc-500 mt-1 text-left">
            Operazioni realmente eseguite e chiuse sul conto MetaTrader (ultime 52 settimane).
          </p>
        </DialogHeader>

        {data && data.enabled !== false && trades.length > 0 && (
          <div className="px-6 py-4 border-b border-white/10 grid grid-cols-3 gap-3">
            <Summ label="Trade chiusi" value={data.count} />
            <Summ label="Vinti / Persi" value={`${data.wins} / ${data.losses}`} color="text-amber-400" />
            <Summ label="P&L totale" value={fmtUsd(data.total_pnl)} color={data.total_pnl >= 0 ? "text-emerald-400" : "text-red-400"} />
          </div>
        )}

        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="text-center py-16 text-zinc-500 text-sm">Caricamento trade MT5...</div>
          ) : data?.enabled === false ? (
            <div className="text-center py-16 text-zinc-500 text-sm">Conto MT5 non collegato.</div>
          ) : data?.error ? (
            <div className="text-center py-16 text-amber-400 text-sm px-6">Connessione in corso o errore: {String(data.error).slice(0, 120)}</div>
          ) : trades.length === 0 ? (
            <div data-testid="mt-closed-empty" className="text-center py-16 text-zinc-500 text-sm">
              Nessun trade chiuso sul conto MT5 al momento.
            </div>
          ) : (
            <div>
              {trades.map((t, i) => {
                const win = t.pnl >= 0;
                const isBuy = (t.type || "").includes("BUY");
                return (
                  <div key={t.id || i} data-testid={`mt-trade-${t.id}`} className="flex items-center gap-3 py-3 px-3 border-b border-white/5 hover:bg-white/5 transition-colors">
                    <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${win ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400"}`}>
                      {win ? <TrendingUp size={15} /> : <TrendingDown size={15} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm text-white">{t.symbol}</span>
                        <span className={`text-xs font-medium ${isBuy ? "text-emerald-400" : "text-red-400"}`}>{(t.type || "").replace("_", " ")}</span>
                        <span className="text-xs text-zinc-500 font-mono">{t.volume} lot</span>
                      </div>
                      <div className="text-[11px] text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap">
                        {t.comment && <span className="text-zinc-400 font-mono">{t.comment}</span>}
                        <span className="text-zinc-700">·</span>
                        <span>{fmtDate(t.time)}</span>
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className={`font-mono text-sm font-semibold ${win ? "text-emerald-400" : "text-red-400"}`}>{fmtUsd(t.pnl)}</div>
                      <div className={`text-[10px] uppercase tracking-wider ${win ? "text-emerald-500/70" : "text-red-500/70"}`}>{win ? "PROFIT" : "LOSS"}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
